import { SetStateAction, useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Tab, Tabs, Box, IconButton, AccordionDetails } from '@mui/material';
import { useGetStormRateById } from 'api/rates';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { LoadingScreen } from 'components/Loaders/LoadingScreen';
import { MdEdit } from 'react-icons/md';
import { useLocation, useParams } from 'react-router-dom';
import { NULL_VALUE } from '../RatesForm/RatesForm.consts';
import { ActionButtons } from './components/ActionButtons';
import { RatesDetailsEdit } from './components/RatesDetailsEdit/RatesDetailsEdit';
import { SideTable } from './components/SideTable';
import * as S from './details.styled';
import {
  collapsedColumnHeaders,
  DATA_LABEL,
  mainColumnHeaders,
  RATE_LABEL,
} from './RatesDetails.consts';
import { FilterParams } from './types';

export const RatesDetails = () => {
  const { ratesId } = useParams<{ ratesId: string }>();

  const [activeTab, setActiveTab] = useState(DATA_LABEL);
  const [isEditing, setIsEditing] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [canBeEdited, setCanBeEdited] = useState(true);
  const [validateAll, setValidateAll] = useState<boolean>(false);

  const location = useLocation();

  const {
    data: stormRateData,
    loading,
    getStormRateById,
  } = useGetStormRateById();

  const getRateById = async (id: string) => {
    await getStormRateById(id);
  };

  useEffect(() => {
    if (ratesId && !isEditing) {
      getRateById(ratesId);
    }
  }, [isEditing, ratesId]);

  useEffect(() => {
    if (stormRateData) {
      setCanBeEdited(!stormRateData.publicationDate);
    }
  }, [stormRateData?.publicationDate]);

  const handleChangeTab = (_: any, selectedTab: SetStateAction<string>) => {
    setActiveTab(selectedTab);
  };

  const handleStartEditing = (event: any) => {
    event.stopPropagation();
    if (!isDetailsOpen) setIsDetailsOpen(true);
    setIsEditing((prev) => !prev);
  };

  const refetchRateData = (rateId: string) => {
    getRateById(rateId);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return stormRateData ? (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <S.RatesDetailsContainer>
        <SideTable
          onRateChange={() => setActiveTab(DATA_LABEL)}
          rate={stormRateData}
        />
        <S.RatesDetailsWrapper>
          <LoaderFullScreen loading={loading} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <S.Title>{`${RATE_LABEL} ${stormRateData.rateNumber}`}</S.Title>
            <ActionButtons
              rateData={stormRateData}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              setValidateAll={setValidateAll}
              refetchRateData={refetchRateData}
            />
          </Box>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              marginBottom: '0.025rem',
            }}
          >
            <Tabs value={activeTab} onChange={handleChangeTab}>
              <Tab label={RATE_LABEL} value={DATA_LABEL} />
            </Tabs>
          </Box>
          {activeTab === DATA_LABEL && (
            <S.StyledAccordion defaultExpanded expanded={isDetailsOpen}>
              <S.IconLeftAccordionSummary
                expandIcon={<ExpandMoreIcon fontSize='large' />}
                onClick={() => setIsDetailsOpen((prev) => !prev)}
              >
                <S.Subtitle>{DATA_LABEL}</S.Subtitle>
                {!isEditing && canBeEdited && (
                  <IconButton
                    size='small'
                    color='inherit'
                    onClick={handleStartEditing}
                  >
                    <MdEdit size={20} />
                  </IconButton>
                )}
              </S.IconLeftAccordionSummary>
              <AccordionDetails>
                {isEditing ? (
                  <RatesDetailsEdit
                    rateData={stormRateData}
                    setIsEditing={setIsEditing}
                    setValidateAll={setValidateAll}
                    validateAll={validateAll}
                  />
                ) : (
                  <S.List>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.rateNumber}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.rateNumber}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.shippingLine}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        {stormRateData?.shippingLine?.counterpartyName || ''}
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.startPort}
                      </S.ListRowTitle>
                      <S.ListRowData>{`${stormRateData.startPort.countryCode} ${stormRateData.startPort.name}`}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.endPort}
                      </S.ListRowTitle>
                      <S.ListRowData>{`${stormRateData.endPort.countryCode} ${stormRateData.endPort.name}`}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.containerType}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        {stormRateData?.container?.code || ''}
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.rateAllIn}
                      </S.ListRowTitle>
                      <S.ListRowData>{`${stormRateData.currencyAmount.value} ${stormRateData.currencyAmount.currencyCode.currency}`}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.dateFrom}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.dateFrom}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.dateTo}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.dateTo}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {mainColumnHeaders.publicationDate}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        {stormRateData.publicationDate}
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.contractNumber}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        {stormRateData.contractNumber}
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.tradeType}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.tradeType}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.commodities}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        <S.ListRowDataInnerList>
                          {stormRateData.commodities.map(
                            (commodity: { id: number; code: string }) => (
                              <li key={commodity.id}>{commodity.code}</li>
                            ),
                          )}
                        </S.ListRowDataInnerList>
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.namedAccounts}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        <S.ListRowDataInnerList>
                          {stormRateData.namedAccounts.length > 0 ? (
                            stormRateData.namedAccounts.map(
                              (namedAccount: { id: number; name: string }) => (
                                <li key={namedAccount.id}>
                                  {namedAccount.name}
                                </li>
                              ),
                            )
                          ) : (
                            <li>{NULL_VALUE}</li>
                          )}
                        </S.ListRowDataInnerList>
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.region}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.region.code}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.stormRateJobs}
                      </S.ListRowTitle>
                      <S.ListRowData>
                        <S.ListRowDataInnerList>
                          {stormRateData.stormRateJobs.map(
                            (stormRateJob: {
                              id: number;
                              activity: { code: string };
                              currencyAmount: {
                                value: number;
                                currencyCode: { currency: string };
                              };
                            }) => (
                              <li key={stormRateJob.id}>
                                {`${stormRateJob.activity.code} | ${stormRateJob.currencyAmount.value} ${stormRateJob.currencyAmount.currencyCode.currency}`}
                              </li>
                            ),
                          )}
                        </S.ListRowDataInnerList>
                      </S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.daysOff}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.daysOff}</S.ListRowData>
                    </S.ListRow>
                    <S.ListRow>
                      <S.ListRowTitle>
                        {collapsedColumnHeaders.note}
                      </S.ListRowTitle>
                      <S.ListRowData>{stormRateData.note}</S.ListRowData>
                    </S.ListRow>
                  </S.List>
                )}
              </AccordionDetails>
            </S.StyledAccordion>
          )}
          <ActionButtons
            refetchRateData={refetchRateData}
            rateData={stormRateData}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            setValidateAll={setValidateAll}
          />
        </S.RatesDetailsWrapper>
      </S.RatesDetailsContainer>
    </Box>
  ) : null;
};
